import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  boardColumnForStatus,
  createRoomTask,
  fetchPulseTaskStatuses,
  fetchRoomTasks,
  nextTaskStatus,
  setTaskStatus,
  type RoomTask,
} from '~/data/interact'
import type { Person, Pulse } from '~/data/types'
import type { EventBus } from '~/realtime/bus'
import { theme } from '~/theme'
import { AgentCharacter, showsCharacter } from './AgentAvatar'

// The room's task board, opened from the wall corkboard. Live: refetches on
// pulse-task events for this room (plus a slow safety interval).

const STATUS_LABELS: Record<string, string> = {
  TODO: 'Open',
  INPROGRESS: 'Doing',
  COMPLETED: 'Done',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
}

const statusColor = (s: string): string =>
  s === 'COMPLETED'
    ? '#54d18c'
    : s === 'INPROGRESS'
      ? '#5b8dd6'
      : s === 'OVERDUE'
        ? '#ff5a5a'
        : '#e0a13a'

export function TasksPanel({
  pulse,
  organizationId,
  people,
  bus,
  getToken,
  onClose,
  onOpenTask,
  compact,
}: {
  pulse: Pulse
  organizationId: string
  people: Person[]
  bus: EventBus
  getToken: GetToken
  onClose: () => void
  onOpenTask: (task: RoomTask) => void
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [draftList, setDraftList] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showDone, setShowDone] = useState(false)
  const client = async () => createClient(await getToken())

  const tasks = useQuery({
    queryKey: ['roomTasks', pulse.id],
    queryFn: async () =>
      fetchRoomTasks(await client(), { organizationId, pulseId: pulse.id }),
    refetchInterval: 45_000,
    refetchOnWindowFocus: false,
  })
  const boardColumns = useQuery({
    queryKey: ['taskColumns', pulse.id],
    queryFn: async () => fetchPulseTaskStatuses(await client(), pulse.id),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  })

  // Real-time: any task event for this pulse refreshes the board.
  useEffect(() => {
    return bus.on((event) => {
      if (event.kind === 'task' && event.pulseId === pulse.id) {
        void queryClient.invalidateQueries({ queryKey: ['roomTasks', pulse.id] })
      }
    })
  }, [bus, pulse.id, queryClient])

  const addTask = useMutation({
    mutationFn: async () => {
      const title = draft.trim()
      if (!title) return
      await createRoomTask(await client(), {
        organizationId,
        pulseId: pulse.id,
        title,
        parentId: draftList || null,
      })
    },
    onSuccess: () => {
      setDraft('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['roomTasks', pulse.id] })
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : 'Could not create the task'),
  })

  const cycleStatus = useMutation({
    mutationFn: async (task: RoomTask) => {
      const next = nextTaskStatus(task.status)
      const column = boardColumnForStatus(boardColumns.data ?? [], next)
      await setTaskStatus(await client(), {
        organizationId,
        taskId: task.id,
        status: next,
        taskStatusId: column?.id ?? null,
      })
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['roomTasks', pulse.id] }),
    onError: (e) =>
      setError(e instanceof Error ? e.message : 'Could not update the task'),
  })

  const personById = new Map(people.map((p) => [p.userId, p]))
  const all = (tasks.data?.tasks ?? []).filter((t) => t.type !== 'LIST')
  const lists = tasks.data?.lists ?? []
  const open = all.filter((t) => t.status !== 'COMPLETED' && t.status !== 'CANCELLED')
  const done = all.filter((t) => t.status === 'COMPLETED')
  const visible = showDone ? [...open, ...done] : open

  const groups: Array<{ key: string; label: string | null; items: RoomTask[] }> = []
  const loose = visible.filter((t) => !t.listId)
  if (loose.length > 0) groups.push({ key: 'loose', label: null, items: loose })
  for (const list of lists) {
    const items = visible.filter((t) => t.listId === list.id)
    if (items.length > 0)
      groups.push({ key: list.id, label: `${list.name} · ${list.open} open`, items })
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>▤ {pulse.name} — tasks</div>
          <div style={{ fontSize: 11, color: theme.textDim }}>
            {open.length} open · {done.length} done
            <button onClick={() => setShowDone((v) => !v)} style={ghostMini}>
              {showDone ? 'hide done' : 'show done'}
            </button>
          </div>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
      </div>

      <div style={{ marginTop: 8 }}>
        {tasks.isLoading && <div style={dim}>Loading the board…</div>}
        {!tasks.isLoading && visible.length === 0 && (
          <div style={dim}>No open tasks — add the first one below.</div>
        )}
        {groups.map((g) => (
          <div key={g.key}>
            {g.label && <div style={listDivider}>▸ {g.label}</div>}
            {g.items.map((t) => {
              const overdue = t.status === 'OVERDUE'
              return (
                <div key={t.id} style={row} onClick={() => onOpenTask(t)}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      cycleStatus.mutate(t)
                    }}
                    disabled={cycleStatus.isPending}
                    title="Click to move status"
                    style={{
                      ...statusChip,
                      color: t.statusColor ?? statusColor(t.status),
                      borderColor: `${t.statusColor ?? statusColor(t.status)}55`,
                    }}
                  >
                    {t.statusLabel ?? STATUS_LABELS[t.status] ?? t.status}
                  </button>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 12.5,
                      color: t.status === 'COMPLETED' ? theme.textDim : theme.text,
                      textDecoration: t.status === 'COMPLETED' ? 'line-through' : 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.title}
                  </span>
                  {t.dueDate && (
                    <span style={{ fontSize: 10, color: overdue ? '#ff8a7a' : theme.textDim }}>
                      {t.dueDate.slice(5, 10)}
                    </span>
                  )}
                  <span style={{ display: 'flex', gap: 2 }}>
                    {t.assignees.slice(0, 3).map((a) => {
                      const p = personById.get(a.userId)
                      // A pictureless agent's character in the dot's box (too small for the emblem).
                      if (showsCharacter(p)) {
                        return <AgentCharacter key={a.userId} userId={a.userId} size={16} title={a.name} />
                      }
                      return (
                        <span key={a.userId} title={a.name} style={avatarDot}>
                          {(p?.name ?? a.name).slice(0, 1)}
                        </span>
                      )
                    })}
                  </span>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addTask.mutate()
          }}
          placeholder="New task…"
          style={{ ...input, flex: 1 }}
        />
        {lists.length > 0 && (
          <select
            value={draftList}
            onChange={(e) => setDraftList(e.target.value)}
            style={{ ...input, width: 110, appearance: 'auto' as never }}
          >
            <option value="">No list</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => addTask.mutate()}
          disabled={addTask.isPending || !draft.trim()}
          style={addBtn}
        >
          +
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: '#ff8a7a', marginTop: 6 }}>{error}</div>}
    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 360,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 14,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const titleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: theme.cream,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const dim: React.CSSProperties = { fontSize: 11.5, color: theme.textDim, padding: '10px 0' }
const listDivider: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  color: theme.terracottaLite,
  margin: '10px 0 2px',
}
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '7px 8px',
  marginTop: 4,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
  cursor: 'pointer',
}
const statusChip: React.CSSProperties = {
  background: 'none',
  border: '1px solid',
  borderRadius: 999,
  fontSize: 9.5,
  fontWeight: 700,
  padding: '2px 8px',
  cursor: 'pointer',
  flex: '0 0 auto',
}
const avatarDot: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#232633',
  color: theme.cream,
  fontSize: 9,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}
const input: React.CSSProperties = {
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  borderRadius: 9,
  fontSize: 12.5,
  padding: '9px 10px',
  outline: 'none',
}
const addBtn: React.CSSProperties = {
  width: 38,
  background: theme.terracotta,
  border: 'none',
  color: '#fff',
  borderRadius: 9,
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
}
const ghostMini: React.CSSProperties = {
  marginLeft: 8,
  background: 'none',
  border: 'none',
  color: theme.terracottaLite,
  fontSize: 10.5,
  cursor: 'pointer',
  padding: 0,
}
